import useRequest from "@/lib/request/useRequest";
import { useState, useMemo, useCallback } from "react";
import { CropConfiguration } from "@/types/cropConfiguration";
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Select,
    SelectItem,
    Button,
} from "@heroui/react";
import dayjs from "dayjs";

export type GrowthStageTarget = {
    field: number;
    crop_zone?: number | null;
};

function BulkSetGrowthStage({
    cropConfigurations,
    targetsByCropConfigurationId,
    onFinish,
}: {
    cropConfigurations: CropConfiguration[];
    targetsByCropConfigurationId: Record<number, GrowthStageTarget[]>;
    onFinish: () => void;
}) {
    const { useMutateRequest } = useRequest();

    const givenCropConfigurations = useMemo(() => {
        return cropConfigurations.filter((cc) =>
            Object.keys(targetsByCropConfigurationId).includes(cc.id.toString()),
        );
    }, [cropConfigurations, targetsByCropConfigurationId]);

    const [selectedStages, setSelectedStages] = useState<Record<number, string>>(
        {},
    );

    const updateGrowthStage = useMutateRequest({
        url: "/reported_growth_stages/bulk_upsert/",
        method: "post",
    });

    const handleSubmit = useCallback(() => {
        const reportedGrowthStages = Object.entries(selectedStages).flatMap(
            ([cropConfigId, growthStage]) => {
                const targets =
                    targetsByCropConfigurationId[Number(cropConfigId)] || [];
                return targets.map((target) => ({
                    field: target.field,
                    crop_zone: target.crop_zone ?? null,
                    growth_stage: growthStage,
                    date: dayjs().format("YYYY-MM-DD"),
                }));
            },
        );
        if (reportedGrowthStages.length === 0) {
            return;
        }
        updateGrowthStage.mutate(
            {
                data: reportedGrowthStages,
            },
            {
                onSuccess: onFinish,
            },
        );
    }, [
        selectedStages,
        targetsByCropConfigurationId,
        updateGrowthStage,
        onFinish,
    ]);

    return (
        <Modal isOpen onClose={onFinish}>
            <ModalContent>
                <ModalHeader>Set Growth Stage</ModalHeader>
                <ModalBody>
                    <div className="flex flex-col gap-4">
                        {givenCropConfigurations.map((cropConfig) => (
                            <div
                                key={cropConfig.id}
                                className="flex flex-col gap-2"
                            >
                                <label className="text-sm font-medium">
                                    {cropConfig.crop_name}
                                </label>
                                <Select
                                    placeholder="Select growth stage"
                                    selectedKeys={
                                        selectedStages[cropConfig.id]
                                            ? [selectedStages[cropConfig.id]]
                                            : []
                                    }
                                    onSelectionChange={(keys) => {
                                        const selectedStage = Array.from(
                                            keys,
                                        )[0] as string;
                                        setSelectedStages((prev) => ({
                                            ...prev,
                                            [cropConfig.id]: selectedStage,
                                        }));
                                    }}
                                >
                                    {cropConfig.growth_stages.map((stage) => (
                                        <SelectItem key={stage}>
                                            {stage}
                                        </SelectItem>
                                    ))}
                                </Select>
                            </div>
                        ))}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button
                        fullWidth
                        color="primary"
                        onPress={handleSubmit}
                        isDisabled={Object.keys(selectedStages).length === 0}
                        isLoading={updateGrowthStage.isPending}
                    >
                        Set Growth Stage
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default BulkSetGrowthStage;
